import type { ConceptMetricField } from "./ConceptType";

/** 沪深京 A 股交易所缩写 */
const A_SHARE_MARKETS = new Set(["SH", "SZ", "BJ"]);

/** 常见美股 ts_code / 后缀（TuShare 等） */
const US_MARKET_SUFFIXES = new Set([
  "US",
  "O",
  "N",
  "A",
  "OQ",
  "OB",
  "NASDAQ",
  "NYSE",
  "AMEX",
  "NSQ",
  "NYS",
]);

interface ParsedStockCode {
  pure: string;
  market: string | null;
}

function inferAShareMarket(pure: string): string | null {
  if (!/^\d{6}$/.test(pure)) {
    return null;
  }
  const first = pure.charAt(0);
  if (first === "6") {
    return "SH";
  }
  if (first === "0" || first === "3") {
    return "SZ";
  }
  if (first === "8" || first === "4" || first === "9") {
    return "BJ";
  }
  return null;
}

function normalizeHkPureCode(pure: string): string {
  if (/^\d+$/.test(pure)) {
    return pure.padStart(5, "0");
  }
  return pure;
}

function parseConceptStockCode(raw: string): ParsedStockCode | null {
  const s = raw.trim().toUpperCase();
  if (!s) {
    return null;
  }

  if (s.includes(".")) {
    const dot = s.indexOf(".");
    const pure = s.slice(0, dot);
    const suffix = s.slice(dot + 1);
    if (A_SHARE_MARKETS.has(suffix)) {
      return { pure, market: suffix };
    }
    if (suffix === "HK") {
      return { pure, market: "HK" };
    }
    if (US_MARKET_SUFFIXES.has(suffix)) {
      return { pure, market: "US" };
    }
    // 带点的美股代码，如 BRK.B
    if (/^[A-Z][A-Z0-9]*$/.test(pure) && /^[A-Z0-9]+$/.test(suffix)) {
      return { pure: `${pure}.${suffix}`, market: "US" };
    }
    return { pure: s, market: null };
  }

  const prefixed = /^(SH|SZ|BJ|HK|US)(.+)$/.exec(s);
  if (prefixed) {
    return { pure: prefixed[2], market: prefixed[1] };
  }

  if (/^\d{6}$/.test(s)) {
    return { pure: s, market: inferAShareMarket(s) };
  }

  if (/^\d{1,5}$/.test(s)) {
    return { pure: s, market: "HK" };
  }

  if (/^[A-Z][A-Z0-9.]*$/.test(s)) {
    return { pure: s, market: "US" };
  }

  return { pure: s, market: null };
}

/**
 * 列表行展示用股票代码：市场缩写 + 代码，如 SH600519、SZ300502、BJ430047、HK00700、USAAPL。
 * 仅处理 A 股 / 港股 / 美股；其他地区返回原码（大写）。
 */
export function formatConceptStockCodeDisplay(stockCode: string): string {
  const parsed = parseConceptStockCode(stockCode);
  if (!parsed) {
    return stockCode.trim();
  }
  const { pure, market } = parsed;
  if (!market) {
    return pure;
  }
  if (market === "HK") {
    return `HK${normalizeHkPureCode(pure)}`;
  }
  return `${market}${pure}`;
}

/** 1 亿（元） */
const YI_YUAN = 100_000_000;
/** 1 万亿（元）= 10000 亿 */
const WAN_YI_YUAN = 10_000 * YI_YUAN;

/** 最多保留 4 位有效数字，去掉多余尾随 0 */
export function formatUpTo4SignificantDigits(n: number): string {
  if (!Number.isFinite(n)) {
    return "—";
  }
  if (n === 0) {
    return "0";
  }
  const abs = Math.abs(n);
  const exponent = Math.floor(Math.log10(abs));
  const scale = 10 ** (3 - exponent);
  const rounded = Math.round(n * scale) / scale;
  const dec = Math.max(0, 3 - exponent);
  if (dec === 0) {
    return String(Math.round(rounded));
  }
  const fixed = rounded.toFixed(dec);
  return fixed.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
}

/**
 * 市值列值：单位元；不足万亿按「亿」展示（表头已标注），达到万亿按「1.2万」形式（万=万亿）。
 */
export function formatMarketCapValue(yuan: number): string {
  if (!Number.isFinite(yuan)) {
    return "—";
  }
  if (Math.abs(yuan) >= WAN_YI_YUAN) {
    return `${formatUpTo4SignificantDigits(yuan / WAN_YI_YUAN)}万`;
  }
  return formatUpTo4SignificantDigits(yuan / YI_YUAN);
}

export function getMetricColumnHeaderLabel(
  field: ConceptMetricField,
  fallbackLabel: string
): string {
  if (field === "marketValue") {
    return "总市值(亿)";
  }
  if (field === "floatMarketValue") {
    return "流通市值(亿)";
  }
  return fallbackLabel;
}
